import Parse from "parse";
import React from "react";
export type QueryResult<T> = {
  result?: T[];
  loading: boolean;
  reload: () => void;
  error?: Error;
  retreivedAt: number;
};
class CacheService {
  private static _instance: CacheService;
  private cache: Record<string, [number, any]> = {};
  private cacheLoading: Record<string, boolean> = {};
  private cacheResolver: Record<string, () => Promise<any>> = {};
  private cacheErrors: Record<string, Error> = {};
  private invalidationTimeout: number;

  private subscribers: Record<string, ((res: QueryResult<any>) => void)[]> = {};
  static getInstance() {
    if (!this._instance) {
      this._instance = new CacheService();
    }
    return this._instance;
  }
  constructor(timeout: number = 5000) {
    CacheService._instance = this;
    this.invalidationTimeout = timeout;
  }

  getCacheItem(key: string, maxAge: number): QueryResult<any> {
    if (
      !this.cacheResolver[key] ||
      (Date.now() - this.cache[key]?.[0] > maxAge && maxAge > 0)
    ) {
      return undefined;
    }
    return {
      result: this.cache[key]?.[1] || undefined,
      loading: this.cacheLoading[key] || false,
      reload: () => {
        this.reloadCacheItem(key);
      },
      error: this.cacheErrors[key],
      retreivedAt: this.cache[key]?.[0] || 0,
    };
  }

  setCacheItem(
    key: string,
    valueResolver: () => Promise<any>
  ): QueryResult<any> {
    if (this.cacheLoading[key]) return;
    this.cacheLoading[key] = true;
    this.cacheResolver[key] = valueResolver;
    valueResolver()
      .then((value) => {
        const retrieved = Date.now();
        this.cacheLoading[key] = false;
        this.cache[key] = [retrieved, value];
        delete this.cacheErrors[key];
        if (this.subscribers[key]) {
          this.subscribers[key].forEach((callback) =>
            callback({
              result: value,
              loading: false,
              reload: () => this.reloadCacheItem(key),
              error: undefined,
              retreivedAt: retrieved,
            })
          );
        }
      })
      .catch((error) => {
        console.error(`Error setting cache item for key ${key}:`, error);
        this.cacheLoading[key] = false;
        this.cacheErrors[key] = error;
      });
    return {
      result: this.cache[key] ? this.cache[key][1] : undefined,
      loading: this.cacheLoading[key],
      reload: () => {
        this.reloadCacheItem(key);
      },
      error: this.cacheErrors[key],
      retreivedAt: this.cache[key] ? this.cache[key][0] : Date.now(),
    };
  }

  reloadCacheItem(key: string) {
    if (this.cacheResolver[key]) {
      this.setCacheItem(key, this.cacheResolver[key]);
    } else {
      console.warn(`No resolver found for cache item with key: ${key}`);
    }
  }

  subscribeToCacheItem(
    key: string,
    callback: (result: QueryResult<any>) => void
  ) {
    if (!this.subscribers[key]) {
      this.subscribers[key] = [];
    }
    this.subscribers[key].push(callback);
    callback(this.getCacheItem(key, 0));
    // console.log("Subscription added", this.subscribers);
    return () => {
      if (this.subscribers[key]) {
        this.subscribers[key] = this.subscribers[key].filter(
          (cb) => cb !== callback
        );
      }
      // console.log("Subscription removed", this.subscribers);
    };
  }
}

async function load(cacheKey: string, query: Parse.Query<any>) {
  return await query.find();
}

export function useParseQueryFirstCached<T extends Parse.Object>(
  query: Parse.Query<T>,
  cacheKey: string,
  evictionTimeout: number = 1000
): Omit<QueryResult<T>, "result"> & { result?: T } {
  const queryFirst = React.useMemo(() => {
    return Parse.Query.fromJSON(query.className, query.toJSON()).limit(
      1
    ) as Parse.Query<T>;
  }, [query]);
  const result = useParseQueryCached<T>(queryFirst, cacheKey, evictionTimeout);
  return {
    ...result,
    result: result?.result?.[0] ? result.result[0] : undefined,
  };
}

export function useParseQueryCached<T extends Parse.Object>(
  query: Parse.Query<T>,
  cacheKey: string,
  evictionTimeout: number = 0
): QueryResult<T> {
  const cancelSubscription = React.useRef<() => void>();
  const [result, setResult] = React.useState<QueryResult<T> | undefined>(
    CacheService.getInstance().getCacheItem(cacheKey, evictionTimeout) ||
      CacheService.getInstance().setCacheItem(cacheKey, () =>
        load(cacheKey, query)
      )
  );

  React.useEffect(() => {
    return () => {
      if (cancelSubscription.current) {
        cancelSubscription.current();
        cancelSubscription.current = undefined;
      }
    };
  }, [cancelSubscription.current]);

  if (!cancelSubscription.current) {
    cancelSubscription.current =
      CacheService.getInstance().subscribeToCacheItem(cacheKey, (value) => {
        setResult(value);
      });
  }

  return result;
}
